<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSuiteStore } from '@/stores/useSuiteStore'
import pandora from '@/assets/logos/logo-pandora.svg'

const route = useRoute()
const router = useRouter()
const suiteStore = useSuiteStore()

const token = route.params.token as string
const password = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const error = ref('')
const success = ref(false)

async function submit() {
  error.value = ''
  if (!password.value || !confirmPassword.value) {
    error.value = 'Veuillez remplir tous les champs.'
    return
  }
  if (password.value !== confirmPassword.value) {
    error.value = 'Les mots de passe ne correspondent pas.'
    return
  }
  if (password.value.length < 10) {
    error.value = 'Le mot de passe doit contenir au moins 10 caractères.'
    return
  }

  loading.value = true
  try {
    const res = await suiteStore.suiteFetch('/auth/user/password/reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password: password.value }),
    })

    if (!res) {
      error.value = 'Une erreur est survenue. Veuillez réessayer.'
      return
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      if (data.message === 'invalid_or_expired_token') {
        error.value = 'Ce lien est invalide ou a expiré. Veuillez refaire une demande.'
      } else if (data.message === 'password_reused') {
        error.value = 'Ce mot de passe a déjà été utilisé. Veuillez en choisir un autre.'
      } else if (data.message?.startsWith('weak_password')) {
        error.value = 'Mot de passe trop faible. Utilisez au moins 10 caractères avec majuscules, minuscules et chiffres.'
      } else {
        error.value = data.message ?? 'Une erreur est survenue.'
      }
      return
    }

    success.value = true
  } catch {
    error.value = 'Une erreur est survenue. Veuillez réessayer.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen flex items-center justify-center bg-base-200">
    <div class="card w-full max-w-md bg-base-100 shadow-xl">
      <div class="card-body items-center text-center">
        <div class="mb-4">
          <component :is="pandora" class="w-32 mx-auto" />
        </div>
        <h2 class="card-title text-xl mb-2">Nouveau mot de passe</h2>

        <template v-if="!success">
          <p class="text-sm text-base-content/60 mb-4">
            Minimum 10 caractères, avec majuscules, minuscules et chiffres.
          </p>

          <form class="w-full space-y-4" @submit.prevent="submit">
            <div class="form-control">
              <label class="label"><span class="label-text">Nouveau mot de passe</span></label>
              <input
                v-model="password"
                type="password"
                placeholder="••••••••••"
                class="input input-bordered w-full"
                autocomplete="new-password"
                required
              />
            </div>

            <div class="form-control">
              <label class="label"><span class="label-text">Confirmer le mot de passe</span></label>
              <input
                v-model="confirmPassword"
                type="password"
                placeholder="••••••••••"
                class="input input-bordered w-full"
                autocomplete="new-password"
                required
              />
            </div>

            <div v-if="error" class="alert alert-error text-sm py-2">
              {{ error }}
            </div>

            <button
              type="submit"
              class="btn btn-primary w-full"
              :disabled="loading"
            >
              <span v-if="loading" class="loading loading-spinner loading-sm"></span>
              {{ loading ? 'Enregistrement...' : 'Enregistrer' }}
            </button>
          </form>
        </template>

        <template v-else>
          <div class="alert alert-success text-sm py-3 mb-4">
            Mot de passe réinitialisé avec succès.
          </div>
          <button class="btn btn-primary w-full" @click="router.push('/auth/login')">
            Se connecter
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
